import { localStorageToken } from "@/tokens";
import { JsonMimeType } from "@lib/ApiClient";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import TokenManagerInterface from "@modules/token_manager/TokenManager/TokenManagerInterface";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import { catchError, tap } from "rxjs";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import { WISHLIST_SAVED_GUEST_TOKEN_STORAGE_KEY } from "../constants/constants";
import {
    wishlistApplicationLoggerToken,
    wishlistAuthApiClientToken,
} from "../tokens";

@singleton()
class WishlistGuestInfoService {
    private readonly tokenManager: TokenManagerInterface;

    constructor(
        private readonly tokenManagerModule: TokenManagerModule,
        @inject(wishlistAuthApiClientToken)
        private readonly wishlistAuthApiClient: ApiClientInterface,
        @inject(localStorageToken) private readonly localStorage: Storage,
        @inject(wishlistApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {
        this.logger.info("WishlistGuestInfoService is initialized");
        this.tokenManager = this.tokenManagerModule.exports.tokenManager;
    }

    isCurrentGuestInfoSaved() {
        try {
            const token = this.localStorage.getItem(
                WISHLIST_SAVED_GUEST_TOKEN_STORAGE_KEY
            );

            if (token === this.tokenManager.getToken()) {
                return true;
            } else {
                this.localStorage.removeItem(
                    WISHLIST_SAVED_GUEST_TOKEN_STORAGE_KEY
                );
                return false;
            }
        } catch (error) {
            this.logger.error(
                "Error getting guest token from localStorage:",
                error
            );
            return false;
        }
    }

    addGuestInfo(email: string) {
        this.logger.debug("WishlistGuestInfoService.addGuestInfo is started");

        return this.wishlistAuthApiClient
            .post({
                url: "/setCustomer",
                body: JSON.stringify({ email }),
                contentType: JsonMimeType,
            })
            .pipe(
                catchError((error: unknown) => {
                    this.logger.debug(
                        "WishlistGuestInfoService.addGuestInfo is errored",
                        {
                            error,
                        }
                    );
                    throw error;
                }),
                tap(() => {
                    const token = this.tokenManager.getToken();
                    if (token) {
                        this.localStorage.setItem(
                            WISHLIST_SAVED_GUEST_TOKEN_STORAGE_KEY,
                            token
                        );
                    }
                })
            );
    }
}

export default WishlistGuestInfoService;
